import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export interface ApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intent: 'approve' | 'reject';
  title?: string;
  description?: string;
  onConfirm: (reason: string) => void | Promise<void>;
  isSubmitting?: boolean;
  requireReason?: boolean;
}

export const ApprovalDialog = ({
  open,
  onOpenChange,
  intent,
  title,
  description,
  onConfirm,
  isSubmitting,
  requireReason,
}: ApprovalDialogProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  // Reset reason whenever the dialog re-opens for a new record.
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const defaultTitle =
    intent === 'approve'
      ? t('efs.masters.actions.approve_title', { defaultValue: '承認確認' })
      : t('efs.masters.actions.reject_title', { defaultValue: '却下確認' });

  const submitLabel =
    intent === 'approve' ? t('efs.common.actions.approve') : t('efs.common.actions.reject');

  const disabled = !!isSubmitting || (requireReason && reason.trim().length === 0);

  const handleSubmit = async () => {
    await onConfirm(reason.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title ?? defaultTitle}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="approval-reason">
            {t('efs.masters.actions.reason', { defaultValue: '理由' })}
          </Label>
          <Textarea
            id="approval-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder={t('efs.masters.actions.reason_placeholder', {
              defaultValue: '監査ログに記録される説明',
            })}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {t('efs.common.actions.cancel')}
          </Button>
          <Button
            type="button"
            variant={intent === 'reject' ? 'destructive' : 'default'}
            onClick={handleSubmit}
            disabled={disabled}
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
